import { Spinner, mediaQueries } from '@tallink/components-lib'
import { useQuery } from '@tallink/more-react-hooks'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useMedia } from 'react-use'

import { getCoupons } from '../actions/actions'
import { CwsH1, CwsH5, CwsSection } from '../cms-web-styleguide-exports'
import UnstyledButton from '../components/common/UnstyledButton'
import Layout from '../components/layout/Layout'
import MobileClubOneLoggedIn from '../components/Sso/MobileClubOneLoggedIn/MobileClubOneLoggedIn'
import CouponAccordion from '../components/userCoupons/CouponAccordion'
import CouponTabs from '../components/userCoupons/CouponTabs'
import NoCoupons from '../components/userCoupons/NoCoupons'
import {
  getCouponsRequested,
  getGroupedCoupons,
  getUserClientId,
  hasExpiredCoupons,
  hasUsedCoupons,
  isClubOneLogged,
  isCouponsLoading,
  isSsoDisabled,
  getCoupons as selectCoupons,
  hasAvailableCoupons as selectHasAvailableCoupons
} from '../selectors/selectors'
import s from './CouponPage.module.scss'

export default function CouponPage() {
  const { t } = useTranslation()
  const clientGuid = useQuery('clientGuid')
  const resToken = useQuery('resToken')
  const clientId = useSelector(getUserClientId)
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState('unused')
  const hasInitiatedFetch = useSelector(getCouponsRequested)
  const [delayedForSsoToCheckUser, setDelayedForSsoToCheckUser] = useState(true)
  const smUp = useMedia(mediaQueries.smUp, true)

  const ssoDisabled = useSelector(isSsoDisabled)
  const userCoupons = useSelector(selectCoupons)
  const groupedUserCoupons = useSelector(getGroupedCoupons)
  const showTabs = useSelector(hasUsedCoupons)
  const showExpiredTab = useSelector(hasExpiredCoupons)
  const hasAvailableCoupons = useSelector(selectHasAvailableCoupons)
  const isLoading = useSelector(isCouponsLoading)
  const clubOneLoggedIn = useSelector(isClubOneLogged)
  const userCouponsReady = Boolean(clientId || clientGuid || resToken) && !isLoading && hasInitiatedFetch
  const noFetchParams = !clientId && !clientGuid && !resToken
  const isPageReady = (userCouponsReady || noFetchParams) && (noFetchParams ? !delayedForSsoToCheckUser : true)

  useEffect(() => {
    if (!clientGuid && !resToken && clientId) dispatch(getCoupons({ clientId }))
    if (!clientGuid && resToken) dispatch(getCoupons({ resToken }))
    if (clientGuid) dispatch(getCoupons({ clientGuid }))
  }, [clientGuid, resToken, clientId, dispatch])

  useEffect(() => {
    const delay = setTimeout(() => setDelayedForSsoToCheckUser(false), 1000)

    return () => clearTimeout(delay)
  }, [])

  const tabCouponCount = groupKey =>
    Object.values(groupedUserCoupons[groupKey] ?? {}).flatMap(byDate => Object.values(byDate).flat()).length

  return (
    <Layout pageTitle={t('mainPage.title')}>
      <CwsSection
        backgroundColor="seafoam"
        className={`cws-d-flex cws-align-items-center cws-d-flex-direction-column cws-pt-xxl cws-pb-m cws-px-m ${s['coupon-page']}`}
        style={{ height: '100%' }}
        variant="div"
      >
        <CwsH1>{t('mainPage.title')}</CwsH1>
        {!isPageReady && (
          <div className={s['spinner-container']}>
            <Spinner className={s.spinner} />
          </div>
        )}

        {isPageReady && (
          <>
            <CwsH5 className="cws-mb-m cws-px-m" textAlign="center">
              <Trans
                components={{
                  login: (
                    <UnstyledButton
                      ariaLabel={t('companyCustomer.login')}
                      className={s['login-button']}
                      onClick={() => !ssoDisabled && window.SSO.open()}
                    />
                  )
                }}
                i18nKey={noFetchParams ? 'couponPage.login.cta' : 'hero.subtitle'}
              />
            </CwsH5>

            {!smUp && clubOneLoggedIn && <MobileClubOneLoggedIn className="mobile-club-one-logged-in-styled" />}

            {showTabs && (
              <CouponTabs
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showExpiredTab={showExpiredTab}
                tabCouponCount={tabCouponCount}
              />
            )}
            {userCoupons.length > 0 && (
              <CouponAccordion activeTab={activeTab} groupedCoupons={groupedUserCoupons[activeTab]} />
            )}
            {activeTab === 'unused' && !hasAvailableCoupons && !noFetchParams && <NoCoupons />}
          </>
        )}
      </CwsSection>
    </Layout>
  )
}
